import * as React from "react";
import { SFDCAuthContext } from "../providers/auth/SFDCAuthProvider";
import useVendorContext from "../providers/vendor/useCurrentVendor";
import { buildAnalyticsIdentity, resolveTenureBand } from "./identity";
import type { AnalyticsIdentity } from "./identity";

/**
 * The analytics identity for the current session.
 *
 * Reads the vendor from `VendorProvider` and the profile name from
 * `SfdcAuthProvider`, both of which hold values decoded from the
 * Salesforce-issued JWT — so this costs no request and cannot disagree with
 * what the authorizer sees.
 *
 * `useVendorContext()`, never `useAuth().roaster`: the latter returns hardcoded
 * mock data ("Premium Coffee Roasters") and would attribute every event in
 * production to a roaster that does not exist.
 *
 * Both providers must be above the component that calls this. Pass the result
 * to `AnalyticsProvider`, which is where it turns into `identify()`, `reset()`
 * and root properties:
 *
 * ```tsx
 * const identity = useAnalyticsIdentity();
 *
 * <AnalyticsProvider app="bcc" isImpersonating={isImpersonating} identity={identity}>
 *   <AppRoutes />
 * </AnalyticsProvider>
 * ```
 *
 * Signing out clears both contexts, which empties the identity, which is what
 * makes `AnalyticsProvider` call `reset()`. That only holds while the provider
 * stays mounted across the sign-out — see its `identity` prop.
 */
export const useAnalyticsIdentity = (): AnalyticsIdentity => {
  const [vendorState] = useVendorContext();
  const [authState] = React.useContext(SFDCAuthContext);

  const vendor = vendorState.currentVendor;
  const { profileName, contactId: actorContactId } = authState;

  // Destructured to primitives before the memo: both providers hand back a
  // fresh state object on every render, and `AnalyticsProvider` rebuilds its
  // transport whenever the identity's values change.
  const { contactId: vendorContactId, vendorKey, region, createdAt } = vendor ?? {};

  // The ACTOR's contact id wins, and the vendor's is only a fallback. The
  // vendor describes the account being acted on: a Breville admin has no
  // vendor until they pick a roaster, and the impersonation endpoint builds
  // its vendor without a contact at all. Reading identity off the vendor would
  // leave every admin and every impersonated session unidentified — never
  // identified, never reset — which is precisely the shared-browser case the
  // reset exists for.
  const contactId = actorContactId ?? vendorContactId;

  // Banded on every render, then memoised on the RESULT rather than on
  // `createdAt`. The band is a function of the current time, so keying the memo
  // on the vendor alone would freeze it for the life of the session and a tab
  // left open across a boundary would keep stamping the old band on every
  // event. Recomputing is one `Date.parse` and some arithmetic, and because the
  // memo below keys on the resulting string the identity stays referentially
  // stable until the band genuinely changes. It updates on the first render
  // after the boundary, not at the stroke of it, which is well inside the
  // tolerance of a day-granularity band.
  const tenureBand = resolveTenureBand(createdAt);

  return React.useMemo(
    () => buildAnalyticsIdentity({ contactId, vendorKey, region, createdAt, profileName, tenureBand }),
    [contactId, vendorKey, region, createdAt, profileName, tenureBand]
  );
};
